import { useState } from 'react';
import { User, Phone, Gamepad2, ShieldCheck, Gamepad } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { validateUsername, validatePhone, validateIgn } from '../utils/validation';
import LoadingOverlay from '../components/LoadingOverlay';

export default function CompleteProfile() {
    const { user } = useAuth();

    const [username, setUsername] = useState('');
    const [phone, setPhone] = useState('');
    const [pubgIgn, setPubgIgn] = useState('');
    const [ffIgn, setFfIgn] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    if (!user) {
        return <div className="page-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const usernameVal = validateUsername(username);
        if (!usernameVal.valid) return setError(usernameVal.message || 'Invalid username');

        const phoneVal = validatePhone(phone);
        if (!phoneVal.valid) return setError(phoneVal.message || 'Invalid phone');

        if (pubgIgn) {
            const pubgVal = validateIgn(pubgIgn);
            if (!pubgVal.valid) return setError(`PUBG IGN: ${pubgVal.message}`);
        }
        if (ffIgn) {
            const ffVal = validateIgn(ffIgn);
            if (!ffVal.valid) return setError(`Free Fire IGN: ${ffVal.message}`);
        }

        if (!agreed) return setError('You must agree to the Terms of Service and Privacy Policy.');

        const igns: { game: string; name: string }[] = [];
        if (pubgIgn.trim()) igns.push({ game: 'PUBG', name: pubgIgn.trim() });
        if (ffIgn.trim()) igns.push({ game: 'Free Fire', name: ffIgn.trim() });

        setLoading(true);
        try {
            const setupUserFn = httpsCallable(functions, 'setupUserProfile');
            await setupUserFn({
                username: username.trim(),
                phone: phone.trim().replace(/[\s-]/g, '') || undefined,
                igns,
            });
            // Force full reload so AuthContext re-evaluates needsProfileSetup
            window.location.href = '/';
        } catch (err: any) {
            console.error('Profile setup error:', err);
            setError(err.message || 'An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            {loading && <LoadingOverlay text="Setting up your account..." />}
            <div className="auth-card animate-in slide-up">
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <div style={{
                        width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', overflow: 'hidden',
                    }}>
                        {user.photoURL ? (
                            <img src={user.photoURL} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <User size={32} color="var(--primary)" />
                        )}
                    </div>
                    <h2 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.8rem', margin: 0 }}>Complete Your Profile</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 8 }}>
                        Welcome, {user.displayName || user.email}! Set up your gaming profile to start competing.
                    </p>
                </div>

                {error && (
                    <div style={{
                        background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '12px',
                        borderRadius: 'var(--radius-sm)', fontSize: '0.9rem', marginBottom: 20,
                        border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <ShieldCheck size={18} style={{ flexShrink: 0 }} />
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div className="form-group">
                        <label className="form-label">Username <span style={{ color: 'var(--danger)' }}>*</span></label>
                        <div className="input-group">
                            <span className="input-icon"><User size={18} /></span>
                            <input type="text" className="form-input" placeholder="Choose a unique username" value={username} onChange={(e) => setUsername(e.target.value)} minLength={3} maxLength={20} required />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Phone Number <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 400 }}>(Optional)</span></label>
                        <div className="input-group">
                            <span className="input-icon"><Phone size={18} /></span>
                            <input type="tel" className="form-input" placeholder="01XXXXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} />
                        </div>
                    </div>

                    <div style={{ height: 1, background: 'var(--border)', margin: '8px 0' }} />

                    <div className="form-group">
                        <label className="form-label">PUBG IGN <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 400 }}>(Optional)</span></label>
                        <div className="input-group">
                            <span className="input-icon"><Gamepad2 size={18} /></span>
                            <input type="text" className="form-input" placeholder="Your PUBG In-Game Name" value={pubgIgn} onChange={(e) => setPubgIgn(e.target.value)} />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Free Fire IGN <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 400 }}>(Optional)</span></label>
                        <div className="input-group">
                            <span className="input-icon"><Gamepad size={18} /></span>
                            <input type="text" className="form-input" placeholder="Your Free Fire In-Game Name" value={ffIgn} onChange={(e) => setFfIgn(e.target.value)} />
                        </div>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginTop: 8 }}>
                        <input
                            type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                            style={{
                                appearance: 'none', width: 20, height: 20, borderRadius: 4,
                                border: `2px solid ${agreed ? 'var(--primary)' : 'var(--border)'}`,
                                background: agreed ? 'var(--primary)' : 'rgba(0,0,0,0.2)',
                                flexShrink: 0, cursor: 'pointer', marginTop: 2, position: 'relative',
                            }}
                        />
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.5 }}>
                            I agree to the <a href="/terms" target="_blank" style={{ color: 'var(--primary)', textDecoration: 'none' }}>Terms of Service</a> and <a href="/privacy" target="_blank" style={{ color: 'var(--primary)', textDecoration: 'none' }}>Privacy Policy</a>.
                        </span>
                    </label>

                    <button type="submit" className="btn btn-primary" style={{ marginTop: 12, width: '100%' }} disabled={loading}>
                        {loading ? 'Setting up...' : '🚀 Complete Profile'}
                    </button>
                </form>
            </div>
        </div>
    );
}
